/**
 * Babylon scene setup. Modular imports only (bundle budget, Section 9).
 * Everything renders unlit/emissive — no lights, no shadows. Distance fog
 * into the space base color hides row recycling (spec Section 7).
 */
import { Engine } from "@babylonjs/core/Engines/engine";
import { Scene } from "@babylonjs/core/scene";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { TargetCamera } from "@babylonjs/core/Cameras/targetCamera";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { GlowLayer } from "@babylonjs/core/Layers/glowLayer";
import "@babylonjs/core/Layers/effectLayerSceneComponent";
import { FOV_BASE_DEG, PALETTE_W1 } from "../config/tuning";
import type { QualityParams } from "../core/quality";

export interface SceneContext {
  engine: Engine;
  scene: Scene;
  camera: TargetCamera;
}

export function createScene(canvas: HTMLCanvasElement): SceneContext {
  const engine = new Engine(canvas, true, { stencil: false, preserveDrawingBuffer: false });
  canvas.tabIndex = 0; // Babylon sets 1 — positive tabindex breaks tab order (a11y)
  const scene = new Scene(engine);

  const base = Color3.FromHexString(PALETTE_W1.spaceBase);
  scene.clearColor = new Color4(base.r, base.g, base.b, 1);
  scene.fogMode = Scene.FOGMODE_LINEAR;
  scene.fogColor = base;
  scene.fogStart = 45;
  scene.fogEnd = 86;
  // Unlit emissive world — skip Babylon's per-frame light/material bookkeeping
  scene.autoClear = true;

  const camera = new TargetCamera("chase", new Vector3(0, 4.2, -9), scene);
  camera.fov = (FOV_BASE_DEG * Math.PI) / 180;
  camera.minZ = 0.5;
  camera.maxZ = 600; // room for the skybox shell (R2-M4); track fog still ends at 86

  window.addEventListener("resize", () => engine.resize());

  return { engine, scene, camera };
}

/** Flat emissive material (the neon look): no lighting, pure color + fog. */
export function makeFlatMaterial(scene: Scene, name: string, hex: string, brightness = 1): StandardMaterial {
  const mat = new StandardMaterial(name, scene);
  mat.disableLighting = true;
  mat.emissiveColor = Color3.FromHexString(hex).scale(brightness * emissiveBoost);
  mat.diffuseColor = Color3.Black();
  mat.specularColor = Color3.Black();
  return mat;
}

// Low tier has no glow — brighter emissive compensates (spec Section 9).
// Set BEFORE materials are created (i.e. before the level loads).
let emissiveBoost = 1;
export function setEmissiveBoost(v: number): void {
  emissiveBoost = v;
}

/** The one allowed expensive post-effect (spec Section 7): a single glow pass. */
let glow: GlowLayer | null = null;
// meshes that must never feed the glow pass (large translucent veils bloom
// into a flood) — survives glow-layer recreation on quality changes
const glowExcluded = new Set<import("@babylonjs/core/Meshes/mesh").Mesh>();

export function excludeFromGlow(mesh: import("@babylonjs/core/Meshes/mesh").Mesh): void {
  glowExcluded.add(mesh);
  glow?.addExcludedMesh(mesh);
}

export function unexcludeFromGlow(mesh: import("@babylonjs/core/Meshes/mesh").Mesh): void {
  glowExcluded.delete(mesh);
}

export function applyGlowQuality(scene: Scene, q: QualityParams): void {
  glow?.dispose();
  glow = null;
  if (q.glow) {
    glow = new GlowLayer("neon-glow", scene, {
      blurKernelSize: q.glowKernel,
      mainTextureRatio: 0.5,
    });
    glow.intensity = q.glowIntensity;
    for (const mesh of glowExcluded) glow.addExcludedMesh(mesh);
  }
  setEmissiveBoost(q.emissiveBoost);
}
